import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { HeartHandshake, Plus, Sparkles, Search, Loader2, Filter } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow, parseISO } from "date-fns";

const VALUE_PILL = (v) => ({
  customer_focus: "bg-blue-50 text-blue-700 border-blue-200",
  ownership: "bg-amber-50 text-amber-700 border-amber-200",
  excellence: "bg-violet-50 text-violet-700 border-violet-200",
  collaboration: "bg-emerald-50 text-emerald-700 border-emerald-200",
}[v] || "bg-slate-50 text-slate-700 border-slate-200");

export default function Recognition() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [framework, setFramework] = useState(null);
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/kudos/wall", { params: filter ? { value_id: filter } : {} });
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    api.get("/cycles").then(async (r) => {
      const active = (r.data.items || []).find((c) => c.status === "active");
      if (active?.framework_snapshot?.values_assessment?.enabled) {
        setFramework(active.framework_snapshot);
      }
    });
    api.get("/employees", { params: { limit: 200 } }).then((r) => setEmployees(r.data.items || []));
  }, []);

  const values = framework?.values_assessment?.values || [];

  return (
    <div className="max-w-5xl space-y-6" data-testid="recognition-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Recognition</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Kudos wall</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Public recognition tied to your team's values. Specific, frequent kudos beat annual praise — go.</p>
        </div>
        {user?.employee_id && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="give-kudos-btn"><Plus className="w-4 h-4 mr-2" /> Give kudos</Button>
            </DialogTrigger>
            <KudosDialog values={values} employees={employees.filter((e) => e.id !== user.employee_id)} onClose={() => { setOpen(false); load(); }} />
          </Dialog>
        )}
      </div>

      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="value-filters">
          <span className="axia-overline mr-2">Filter:</span>
          <button
            onClick={() => setFilter("")}
            className={`axia-pill ${!filter ? "bg-slate-900 text-white border-slate-900" : "bg-slate-50 text-slate-700 border-slate-200"}`}
          >
            All
          </button>
          {values.map((v) => (
            <button
              key={v.id}
              onClick={() => setFilter(v.id)}
              className={`axia-pill ${filter === v.id ? "bg-slate-900 text-white border-slate-900" : VALUE_PILL(v.id)}`}
              data-testid={`filter-${v.id}`}
            >
              {v.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <HeartHandshake className="w-7 h-7 mx-auto text-slate-300" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">No kudos yet</div>
          <p className="text-sm mt-1">Be the first to recognise a teammate.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((k) => (
            <KudosCard key={k.id} kudo={k} valueLabel={values.find((v) => v.id === k.value_id)?.name} />
          ))}
        </div>
      )}
    </div>
  );
}

function KudosCard({ kudo, valueLabel }) {
  const ago = (() => {
    try {
      return formatDistanceToNow(parseISO(kudo.created_at), { addSuffix: true });
    } catch {
      return "";
    }
  })();
  return (
    <article className="axia-card p-5 hover:border-slate-300 transition" data-testid={`kudos-${kudo.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">
          {(kudo.giver?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm">
            <span className="font-semibold text-slate-900">{kudo.giver?.name}</span>
            <span className="text-slate-500"> recognised </span>
            <span className="font-semibold text-slate-900">{kudo.recipient?.name}</span>
            {kudo.value_id && (
              <span className={`ml-2 axia-pill ${VALUE_PILL(kudo.value_id)}`}>
                <Sparkles className="w-3 h-3" /> {valueLabel || kudo.value_id}
              </span>
            )}
          </div>
          <p className="mt-2 text-[15px] text-slate-800 leading-relaxed">{kudo.message}</p>
          <div className="mt-3 text-[11px] text-slate-400">{ago}</div>
        </div>
      </div>
    </article>
  );
}

function KudosDialog({ values, employees, onClose }) {
  const [recipientId, setRecipientId] = useState("");
  const [valueId, setValueId] = useState("");
  const [message, setMessage] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return employees
      .filter((e) =>
        !q ||
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.designation?.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [employees, search]);

  const submit = async () => {
    if (!recipientId) return toast.error("Pick a recipient");
    if (message.trim().length < 4) return toast.error("Add a message (4+ chars)");
    setSaving(true);
    try {
      await api.post("/kudos", { given_to: recipientId, value_id: valueId || null, message, visibility });
      toast.success("Kudos sent");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const recipient = employees.find((e) => e.id === recipientId);

  return (
    <DialogContent className="max-w-lg" data-testid="kudos-dialog">
      <DialogHeader>
        <DialogTitle>Give kudos</DialogTitle>
        <DialogDescription>Specific, behaviour-based recognition lands harder than generic praise.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Recipient</Label>
          <Command className="border border-slate-200 rounded-md">
            <CommandInput placeholder="Search by name or designation…" value={search} onValueChange={setSearch} data-testid="kudos-search" />
            <CommandList className="max-h-48">
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {filtered.map((e) => (
                  <CommandItem
                    key={e.id}
                    onSelect={() => setRecipientId(e.id)}
                    className={recipientId === e.id ? "bg-slate-100" : ""}
                    data-testid={`kudos-recipient-${e.id}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-semibold mr-2">
                      {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{e.first_name} {e.last_name}</div>
                      <div className="text-[11px] text-slate-500">{e.designation}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {recipient && (
            <div className="text-[11px] text-emerald-700 mt-1.5">Selected: {recipient.first_name} {recipient.last_name}</div>
          )}
        </div>
        {values.length > 0 && (
          <div>
            <Label>Tag a value (optional)</Label>
            <Select value={valueId} onValueChange={setValueId}>
              <SelectTrigger data-testid="kudos-value-select"><SelectValue placeholder="Pick a value" /></SelectTrigger>
              <SelectContent>
                {values.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Message</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="What did they do, and why did it matter?" data-testid="kudos-message" />
        </div>
        <div>
          <Label>Visibility</Label>
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger data-testid="kudos-visibility-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public — appears on the wall</SelectItem>
              <SelectItem value="team">Team only</SelectItem>
              <SelectItem value="private">Private — recipient only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="kudos-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Send kudos
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
